import { memo, useContext, type JSX } from 'react';

import { srollToTop } from '@/shared/lib';
import { ButtonVariant, Icon, Link, LinkAsButton, Typography } from '@/shared/ui';

import { config } from '@/config/index';

import { ROUTES } from '@/router/config';

import AuthContext from '@/contexts/Auth/AuthContext';

import localStorageController from '../../../../Cache';
import { queryClient, PERSISTED_CACHE_KEY } from '@/lib/queryClient';
import { AccessibilityPanelButton } from '../../AccessibilityPanel/ui/AccessibilityPanelButton';

import './FooterStyles.scss';

export const Footer = memo((): JSX.Element => {
  const { storedLinks } = useContext(AuthContext);
  const appStoreLink = storedLinks?.externalLink?.appStore ?? '';
  const googlePlayLink = storedLinks?.externalLink?.googlePlay ?? '';
  const supportPhoneNumber = storedLinks?.serviceInformer?.supportPhoneNumber ?? '';

  const hasAppStoreLink = Boolean(appStoreLink);
  const hasGooglePlayLink = Boolean(googlePlayLink);
  const showMobileAppsBlock = hasAppStoreLink || hasGooglePlayLink;

  const handleAccessibilityClick = (): void => {
    srollToTop();
  };

  const handleClearCache = (): void => {
    localStorageController.clearCachedData();
    window.localStorage.removeItem(PERSISTED_CACHE_KEY);
    queryClient.clear();
    alert('Кеш очищено!');
    window.location.reload();
  };

  return (
    <footer className="footer" id="main-footer" role="contentinfo">
      <div className="container">
        <LinkAsButton variant={ButtonVariant.ICON_GHOST} to={ROUTES.HOME} className="footer__title">
          <Icon name="evg_logo" color="white" width={150} height={50} />
        </LinkAsButton>
        <div className="footer__links">
          <div className="footer__links-licence">
            <Typography normal color="grey">
              Якщо не зазначено інше всі матеріали розміщені на умовах ліцензії
            </Typography>
            <Link className="footer__links-licence-link" to={storedLinks?.externalLink?.licence}>
              Creative Commons Attribution 4.0 International license
            </Link>
          </div>
          <div className="footer__links-contacts">
            <div className="footer__links-contacts-group">
              <Typography normal color="white" className="footer__links-text">
                Контактний центр Єдиного реєстру зброї:
                <Typography as="span" normal color="white" className="footer__mobile-next-line">
                  {supportPhoneNumber}
                </Typography>
              </Typography>
              <Link className="footer__links-contacts-link" to={`mailto:${config.support.email}`}>
                Довідка щодо функціонування порталу:
                <Typography as="span" normal color="white" className="footer__mobile-next-line">
                  {config.support.email}
                </Typography>
              </Link>
              <Link className="footer__links-contacts-link" to={ROUTES.PERSONAL_DATA} target="_blank" rel="noreferrer">
                Політика обробки персональних даних
              </Link>
            </div>
          </div>
        </div>

        <div className="footer__links">
          {showMobileAppsBlock && (
            <div className="footer__links-mobile-apps">
              <div className="footer__links-mobile-apps-text">
                <Typography normal color="white" className="footer__links-text">
                  Встановлюйте застосунок
                  <br />
                  &quot;Соціальний захист&quot;
                </Typography>
              </div>
              <div className="footer__links-mobile-apps-buttons">
                {hasAppStoreLink && (
                  <Link
                    className="footer__links-mobile-apps-button"
                    to={appStoreLink}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Icon name="download_app_store" width={106} height={28} />
                  </Link>
                )}
                {hasGooglePlayLink && (
                  <Link
                    className="footer__links-mobile-apps-button"
                    to={googlePlayLink}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Icon name="download_google_play" width={122} height={28} />
                  </Link>
                )}
              </div>
            </div>
          )}

          <div className="footer__links-additional">
            <AccessibilityPanelButton onClick={handleAccessibilityClick} />
            <button
              className="cache-btn custom-accessibility-button custom-accessibility-button--white"
              type="button"
              onClick={handleClearCache}
            >
              <Icon name="clear_all" />
              <Typography data-testid="typography" className="custom-accessibility-button__text">
                Очистити кеш
              </Typography>
            </button>
            <div className="footer__links-socials">
              <Link
                aria-label="Facebook МВС"
                className="footer__links-social-link"
                to={storedLinks?.externalLink?.mvsFacebook}
              >
                <Icon className="footer__links-social-icon" name="facebook" width={24} height={24} />
              </Link>
              <Link
                aria-label="Twitter МВС"
                className="footer__links-social-link"
                to={storedLinks?.externalLink?.mvsTwitter}
              >
                <Icon className="footer__links-social-icon" name="x" width={24} height={23} />
              </Link>
              <Link
                aria-label="Youtube МВС"
                className="footer__links-social-link"
                to={storedLinks?.externalLink?.mvsYoutube}
              >
                <Icon className="footer__links-social-icon" name="youtube" width={24} height={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});
